import {
  EV_CHARGE_COMPONENT_LABELS,
  EV_IDENTIFICATION_LABELS,
  EV_PAYMENT_METHOD_LABELS,
  type EvSessionDetail,
  rooms,
} from "@station/contracts";
import { Decimal, Money } from "@station/core";
import { Alert, Button, Card, MoneyText, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CircleStop, RefreshCw, Square } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CommandsTable } from "../../../../features/ev/command-table.js";
import {
  describeBand,
  formatKwh,
  formatLineQuantity,
  formatPercent,
  formatRate,
  formatWatts,
  isSessionLive,
  meterChartOption,
  meterRows,
  sessionTimeline,
} from "../../../../features/ev/ev-values.js";
import { useEvPermissions, useEvSession } from "../../../../features/ev/queries.js";
import {
  CompleteSessionDialog,
  StopSessionDialog,
  useRetryInvoice,
} from "../../../../features/ev/session-actions.js";
import {
  evCrumb,
  EvPage,
  Field,
  InvoiceStatusBadge,
  PaymentStatusBadge,
  SessionPhaseBadge,
  SessionWarnings,
  TableFrame,
  Th,
} from "../../../../features/ev/shared.js";
import { ChartWithTable } from "../../../../features/fuel/shared.js";
import { formatDuration } from "../../../../features/wash/wash-values.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../../lib/format.js";
import { useRoom } from "../../../../lib/realtime-context.js";

const searchSchema = z.object({ station: z.string().uuid().optional().catch(undefined) });

export const Route = createFileRoute("/_app/sarj/oturumlar/$id")({
  validateSearch: (search) => searchSchema.parse(search),
  component: SessionRoute,
});

/**
 * Şarj oturumu ayrıntısı (Prompt 19 web, `GET /ev/sessions/{id}`): zaman çizelgesi (başlangıç → şarj bitişi → durma →
 * tamamlanma), kWh / süreler, güç ve enerji grafiği (seyreltilmiş ölçüm serisi; erişilebilir tablo alternatifiyle), yük
 * yöneticisinin uyguladığı güç sınırları, tutar kalemleri (enerji / süre / bekleme), tarife anlık görüntüsü, ödeme (kasa
 * satışı / fatura bağı), uyarılar (Türkçe) ve komutlar. Kişisel alanlar API'nin maskelediği gibi. Eylemler: uzaktan durdur ve
 * takılı oturumu kapat (`ev.operate`), faturayı yeniden dene (`ev.manage`, yalnızca `FAILED`).
 */
function SessionRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const permissions = useEvPermissions();
  const session = useEvSession(id);
  const retryInvoice = useRetryInvoice();
  const [dialog, setDialog] = useState<"stop" | "complete" | null>(null);
  const [retrying, setRetrying] = useState(false);
  useRoom(session.data ? rooms.station(session.data.stationId) : null);
  const data = session.data;
  const live = data ? isSessionLive(data.phase) : false;
  const canStop =
    Boolean(data) &&
    permissions.canOperate &&
    data!.source === "OCPP" &&
    data!.transactionId !== null &&
    (data!.phase === "STARTED" || data!.phase === "CHARGING" || data!.phase === "FAULTED");
  const canComplete = Boolean(data) && permissions.canOperate && data!.source === "OCPP" && live;
  const canRetry = Boolean(data) && permissions.canManage && data!.invoiceStatus === "FAILED";
  const title = data
    ? t("ev.sessionDetail.title", { at: formatDateTime(data.startedAt) })
    : t("menu.evSessions");

  return (
    <EvPage
      title={title}
      description={
        data
          ? data.source === "PARTNER"
            ? `${data.stationName} · ${t("ev.sessions.partner", { code: data.partnerCode ?? "—" })}`
            : `${data.stationName} · ${data.chargerName ?? "—"} · ${t("ev.connector.no", { no: data.connectorNo ?? "—" })}`
          : undefined
      }
      breadcrumb={[
        evCrumb(t("menu.evSessions"), "/sarj/oturumlar", search.station ? { station: search.station } : {}),
        { label: data ? formatDateTime(data.startedAt) : "…" },
      ]}
      actions={
        data ? (
          <div className="flex flex-wrap gap-2">
            {canStop ? (
              <Button onClick={() => setDialog("stop")}>
                <Square aria-hidden="true" />
                {t("ev.sessionActions.stop")}
              </Button>
            ) : null}
            {canComplete ? (
              <Button variant="outline" onClick={() => setDialog("complete")}>
                <CircleStop aria-hidden="true" />
                {t("ev.sessionActions.completeOpen")}
              </Button>
            ) : null}
            {canRetry ? (
              <Button
                variant="outline"
                loading={retrying}
                onClick={() => {
                  setRetrying(true);
                  void retryInvoice(data.id).finally(() => setRetrying(false));
                }}
              >
                <RefreshCw aria-hidden="true" />
                {t("ev.sessionActions.retryInvoice")}
              </Button>
            ) : null}
          </div>
        ) : null
      }
    >
      {session.error ? (
        <Alert tone="danger">{errorMessage(session.error)}</Alert>
      ) : !data ? (
        <SkeletonRows rows={8} />
      ) : (
        <>
          <SessionBody session={data} />
          {dialog === "stop" ? (
            <StopSessionDialog open onOpenChange={(open) => !open && setDialog(null)} session={data} />
          ) : null}
          {dialog === "complete" ? (
            <CompleteSessionDialog open onOpenChange={(open) => !open && setDialog(null)} session={data} />
          ) : null}
        </>
      )}
    </EvPage>
  );
}

function SessionBody({ session }: { session: EvSessionDetail }): ReactElement {
  const { t } = useTranslation();
  const permissions = useEvPermissions();
  const timeline = sessionTimeline(session);
  const rows = useMemo(() => meterRows(session.meterSeries, session.meterStartWh), [session]);
  const option = useMemo(
    () =>
      meterChartOption(rows, session.limits, {
        power: t("ev.sessionDetail.chart.power"),
        energy: t("ev.sessionDetail.chart.energy"),
        soc: t("ev.sessionDetail.chart.soc"),
        limit: t("ev.sessionDetail.chart.limit"),
        powerAxis: t("ev.sessionDetail.chart.powerAxis"),
        energyAxis: t("ev.sessionDetail.chart.energyAxis"),
      }),
    [rows, session.limits, t],
  );
  const linesTotal = Money.sum(session.priceLines.map((line) => Money.fromMinor(line.amount)));
  const snapshot = session.tariffSnapshot as {
    energyPrice?: string;
    minutePrice?: string;
    idlePrice?: string;
    idleGraceMinutes?: number;
    idleCap?: string | null;
    bands?: Array<{ weekdays: number[]; from: string; to: string; energyPrice: string }>;
    validFrom?: string;
  } | null;
  const weekdays = [1, 2, 3, 4, 5, 6, 7].map((day) => t(`ev.weekdays.${day}`));
  const identity = [
    session.counterpartyName,
    session.customerName,
    session.vehiclePlate ? t("ev.sessionDetail.plate", { plate: session.vehiclePlate }) : null,
  ].filter((value): value is string => Boolean(value));

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="grid content-start gap-3 p-4" data-testid="ev-session-summary">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold">{t("ev.sessionDetail.summary")}</h2>
            <SessionPhaseBadge phase={session.phase} />
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label={t("ev.fields.energy")}>
              <span className="tabular text-lg">{formatKwh(session.energyKwh)}</span>
            </Field>
            <Field label={t("ev.fields.totalAmount")}>
              <MoneyText value={session.totalAmount} className="text-lg" />
            </Field>
            <Field label={t("ev.sessionDetail.chargingDuration")}>
              <span className="tabular">{formatDuration(session.chargingSeconds)}</span>
            </Field>
            <Field label={t("ev.sessionDetail.idleDuration")}>
              <span className="tabular">{formatDuration(session.idleSeconds)}</span>
            </Field>
            <Field label={t("ev.sessionDetail.maxPower")}>
              <span className="tabular">{formatWatts(session.maxPowerW)}</span>
            </Field>
            <Field label={t("ev.fields.soc")}>
              <span className="tabular">
                {session.socStart === null && session.socLast === null
                  ? "—"
                  : `${formatPercent(session.socStart, 0)} → ${formatPercent(session.socLast, 0)}`}
              </span>
            </Field>
            <Field label={t("ev.sessionDetail.meter")}>
              <span className="tabular text-xs">
                {session.meterStartWh === null
                  ? "—"
                  : `${Decimal.from(session.meterStartWh).formatTr(0)} → ${
                      session.meterStopWh === null ? "…" : Decimal.from(session.meterStopWh).formatTr(0)
                    } Wh`}
              </span>
            </Field>
            <Field label={t("ev.sessionDetail.stopReason")}>{session.stopReason ?? "—"}</Field>
            {Decimal.from(session.coveredKwh).isPositive() ? (
              <Field className="col-span-2" label={t("ev.sessionDetail.covered")}>
                <span className="tabular">{formatKwh(session.coveredKwh)}</span>
              </Field>
            ) : null}
          </dl>
        </Card>

        <Card className="grid content-start gap-3 p-4">
          <h2 className="text-base font-semibold">{t("ev.sessionDetail.identification")}</h2>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label={t("ev.fields.identification")}>
              {EV_IDENTIFICATION_LABELS[session.identification]}
            </Field>
            <Field label={t("ev.sessionDetail.idTag")}>
              <span className="font-mono">{session.idTag ?? "—"}</span>
            </Field>
            <Field className="col-span-2" label={t("ev.sessionDetail.customer")}>
              {identity.length === 0 ? (
                "—"
              ) : session.customerProfileId && permissions.canOpenCustomer ? (
                <Link
                  to="/musteriler/$id"
                  params={{ id: session.customerProfileId }}
                  className="underline underline-offset-4"
                >
                  {identity.join(" · ")}
                </Link>
              ) : (
                identity.join(" · ")
              )}
            </Field>
            {session.transactionId !== null ? (
              <Field label={t("ev.sessionDetail.transactionId")}>
                <span className="tabular">{session.transactionId}</span>
              </Field>
            ) : null}
            {session.partnerSessionRef ? (
              <Field label={t("ev.sessionDetail.partnerRef")}>
                <span className="font-mono text-xs">{session.partnerSessionRef}</span>
              </Field>
            ) : null}
            <Field label={t("ev.fields.source")}>{t(`ev.source.${session.source}`)}</Field>
            {session.personalDataErased ? (
              <Field className="col-span-2" label={t("ev.sessionDetail.kvkk")}>
                <StatusBadge tone="neutral">{t("ev.sessionDetail.erased")}</StatusBadge>
              </Field>
            ) : null}
          </dl>
        </Card>

        <Card className="grid content-start gap-3 p-4" data-testid="ev-session-payment">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold">{t("ev.sessionDetail.payment")}</h2>
            <PaymentStatusBadge status={session.paymentStatus} />
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label={t("ev.sessionDetail.method")}>
              {session.paymentMethod ? EV_PAYMENT_METHOD_LABELS[session.paymentMethod] : "—"}
            </Field>
            <Field label={t("ev.sessionDetail.paidAt")}>{formatDateTime(session.paidAt)}</Field>
            {session.posSale ? (
              <Field className="col-span-2" label={t("ev.sessionDetail.posSale")}>
                <span className="grid gap-0.5">
                  {permissions.canOpenPosSale ? (
                    <Link
                      to="/kasa/satislar/$id"
                      params={{ id: session.posSale.id }}
                      className="underline underline-offset-4"
                    >
                      {t("ev.sessionDetail.posSaleLink", { at: formatDateTime(session.posSale.occurredAt) })}
                    </Link>
                  ) : (
                    t("ev.sessionDetail.posSaleLink", { at: formatDateTime(session.posSale.occurredAt) })
                  )}
                  <span className="text-xs text-muted-foreground">
                    {session.posSale.agentSaleId}
                    {session.posSale.lineNo !== null ? ` · #${session.posSale.lineNo}` : ""}
                  </span>
                </span>
              </Field>
            ) : null}
            {session.invoiceStatus !== "NONE" || session.invoice ? (
              <Field className="col-span-2" label={t("ev.sessionDetail.invoice")}>
                <span className="grid justify-items-start gap-1">
                  <InvoiceStatusBadge status={session.invoiceStatus} />
                  {session.invoice ? (
                    permissions.canOpenInvoice ? (
                      <Link
                        to="/faturalar/$id"
                        params={{ id: session.invoice.id }}
                        className="underline underline-offset-4"
                      >
                        {session.invoice.invoiceNo ?? t("ev.sessionDetail.invoiceDraft")}
                      </Link>
                    ) : (
                      <span>{session.invoice.invoiceNo ?? t("ev.sessionDetail.invoiceDraft")}</span>
                    )
                  ) : null}
                  {session.invoice ? (
                    <span className="text-xs text-muted-foreground">
                      {formatDate(session.invoice.issueDate)}
                      {session.invoice.scenario ? ` · ${session.invoice.scenario}` : ""} ·{" "}
                      <MoneyText value={session.invoice.payableAmount} />
                    </span>
                  ) : null}
                  {session.invoiceError ? (
                    <span className="text-xs text-destructive">
                      {t("ev.sessionDetail.invoiceError", {
                        attempts: session.invoiceAttempts,
                        error: session.invoiceError,
                      })}
                    </span>
                  ) : null}
                </span>
              </Field>
            ) : null}
          </dl>
          {session.paymentStatus === "UNPAID" && session.phase === "COMPLETED" ? (
            <p className="text-xs text-muted-foreground">{t("ev.sessionDetail.awaitingPos")}</p>
          ) : null}
        </Card>
      </div>

      {session.warnings.length > 0 ? (
        <Card className="grid gap-2 p-4" data-testid="ev-session-warnings">
          <h2 className="text-base font-semibold">{t("ev.sessionDetail.warnings")}</h2>
          <SessionWarnings warnings={session.warnings} />
        </Card>
      ) : null}

      <Card className="grid gap-3 p-4">
        <h2 className="text-base font-semibold">{t("ev.sessionDetail.timeline")}</h2>
        <ol className="grid gap-2 sm:grid-cols-4" data-testid="ev-session-timeline">
          {timeline.map((entry) => (
            <li
              key={entry.step}
              className={`grid gap-0.5 rounded-md border px-3 py-2 ${entry.at ? "" : "border-dashed text-muted-foreground"}`}
            >
              <span className="text-xs text-muted-foreground">
                {t(`ev.sessionDetail.step.${entry.step}`)}
              </span>
              <span className="tabular text-sm font-medium">
                {entry.at ? formatDateTime(entry.at) : t("ev.sessionDetail.pending")}
              </span>
              {entry.sincePreviousSeconds !== null && entry.step !== "started" ? (
                <span className="text-xs text-muted-foreground">
                  +{formatDuration(entry.sincePreviousSeconds)}
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      </Card>

      {rows.length > 1 ? (
        <Card className="min-w-0 p-4">
          <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-base font-semibold">{t("ev.sessionDetail.chartTitle")}</h2>
            <span className="text-xs text-muted-foreground">
              {t("ev.sessionDetail.samples", { shown: rows.length, total: session.meterSampleCount })}
            </span>
          </div>
          <ChartWithTable
            ariaLabel={t("ev.sessionDetail.chartAria", { energy: formatKwh(session.energyKwh) })}
            option={option}
            height={320}
            tableLabel={t("ev.sessionDetail.chartTable")}
          >
            <table className="w-full text-sm">
              <caption className="sr-only">{t("ev.sessionDetail.chartTable")}</caption>
              <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
                <tr>
                  <Th>{t("ev.sessionDetail.time")}</Th>
                  <Th align="right">{t("ev.fields.power")}</Th>
                  <Th align="right">{t("ev.fields.energy")}</Th>
                  <Th align="right">{t("ev.fields.soc")}</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.at} className="border-t">
                    <td className="px-3 py-1.5 tabular whitespace-nowrap">{formatDateTime(row.at)}</td>
                    <td className="px-3 py-1.5 text-right tabular">
                      {row.powerKw === null ? "—" : `${Decimal.from(row.powerKw).formatTr(2)} kW`}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular">{formatKwh(row.energyKwh)}</td>
                    <td className="px-3 py-1.5 text-right tabular">{formatPercent(row.socPercent, 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ChartWithTable>
        </Card>
      ) : null}

      <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0" data-testid="ev-price-lines">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-base font-semibold">{t("ev.sessionDetail.lines")}</h2>
          {session.tariffName ? (
            <span className="text-sm text-muted-foreground">
              {t("ev.sessionDetail.tariffName", { name: session.tariffName })}
            </span>
          ) : null}
        </div>
        {session.priceLines.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {session.phase === "COMPLETED"
              ? t("ev.sessionDetail.noLines")
              : t("ev.sessionDetail.linesPending")}
          </p>
        ) : (
          <TableFrame label={t("ev.sessionDetail.lines")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("ev.sessionDetail.component")}</Th>
                <Th>{t("ev.sessionDetail.product")}</Th>
                <Th align="right">{t("ev.sessionDetail.quantity")}</Th>
                <Th align="right">{t("ev.sessionDetail.unitPrice")}</Th>
                <Th align="right">{t("ev.sessionDetail.amount")}</Th>
              </tr>
            </thead>
            <tbody>
              {session.priceLines.map((line) => (
                <tr key={line.component} className="border-t">
                  <th scope="row" className="px-3 py-2 text-left font-medium">
                    {EV_CHARGE_COMPONENT_LABELS[line.component]}
                  </th>
                  <td className="px-3 py-2">{line.productName ?? "—"}</td>
                  <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                    {formatLineQuantity(line.quantity, line.unit)}
                  </td>
                  <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                    {formatRate(line.unitPrice, line.unit)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <MoneyText value={line.amount} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 font-semibold">
              <tr>
                <th scope="row" colSpan={4} className="px-3 py-2 text-left">
                  {t("common.total")}
                </th>
                <td className="px-3 py-2 text-right">
                  <MoneyText value={linesTotal.minor.toString()} />
                </td>
              </tr>
            </tfoot>
          </TableFrame>
        )}
        {snapshot ? (
          <div className="grid gap-2 rounded-md border p-3" data-testid="ev-tariff-snapshot">
            <h3 className="text-sm font-semibold">{t("ev.sessionDetail.snapshot")}</h3>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
              <Field label={t("ev.tariffs.fields.energyPrice")}>
                {formatRate(snapshot.energyPrice, "KWH")}
              </Field>
              <Field label={t("ev.tariffs.fields.minutePrice")}>
                {formatRate(snapshot.minutePrice, "MINUTE")}
              </Field>
              <Field label={t("ev.tariffs.fields.idlePrice")}>
                {formatRate(snapshot.idlePrice, "MINUTE")}
              </Field>
              <Field label={t("ev.tariffs.fields.idleGrace")}>
                {t("ev.tariffs.minutes", { count: snapshot.idleGraceMinutes ?? 0 })}
              </Field>
              <Field label={t("ev.tariffs.fields.idleCap")}>
                {snapshot.idleCap ? `${Decimal.from(snapshot.idleCap).formatTr(2)} ₺` : t("ev.tariffs.noCap")}
              </Field>
              {snapshot.bands && snapshot.bands.length > 0 ? (
                <Field className="col-span-2 sm:col-span-3" label={t("ev.tariffs.fields.bands")}>
                  <span className="grid gap-0.5">
                    {snapshot.bands.map((band) => (
                      <span key={`${band.weekdays.join(",")}-${band.from}`}>
                        {describeBand(band, weekdays, t("ev.tariffs.everyDay"))}
                      </span>
                    ))}
                  </span>
                </Field>
              ) : null}
            </dl>
          </div>
        ) : null}
      </Card>

      {session.limits.length > 0 ? (
        <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0">
          <h2 className="text-base font-semibold">{t("ev.sessionDetail.limits")}</h2>
          <TableFrame label={t("ev.sessionDetail.limits")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("ev.sessionDetail.time")}</Th>
                <Th>{t("ev.load.method")}</Th>
                <Th align="right">{t("ev.load.available")}</Th>
                <Th align="right">{t("ev.fields.limit")}</Th>
                <Th>{t("ev.load.reasonLabel")}</Th>
              </tr>
            </thead>
            <tbody>
              {session.limits.map((limit) => (
                <tr key={limit.id} className="border-t">
                  <td className="px-3 py-2 tabular whitespace-nowrap">{formatDateTime(limit.at)}</td>
                  <td className="px-3 py-2">{t(`ev.loadMethod.${limit.method}`)}</td>
                  <td className="px-3 py-2 text-right tabular">{formatWatts(limit.availableW)}</td>
                  <td className="px-3 py-2 text-right tabular whitespace-nowrap">
                    {limit.previousLimitW !== null ? `${formatWatts(limit.previousLimitW)} → ` : ""}
                    {limit.suspended ? t("ev.load.suspended") : formatWatts(limit.limitW)}
                  </td>
                  <td className="px-3 py-2">{t(`ev.limitReason.${limit.reason}`)}</td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        </Card>
      ) : null}

      {session.commands.length > 0 ? (
        <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0">
          <h2 className="text-base font-semibold">{t("ev.sessionDetail.commands")}</h2>
          <CommandsTable commands={session.commands} label={t("ev.sessionDetail.commands")} />
        </Card>
      ) : null}
    </>
  );
}
